import { Dispatch } from 'redux';
import { DEFAULT_RETRY_COUNT, finalRiskApi, riskApi } from '../apis';
import { fetchFinalRiskLevelsSuccess, fetchRiskOverrideReasonPageSuccess } from './riskReducer';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { InternalApiAddFinalRiskOverride } from '../api-internal';
import { useMe } from '../organisation/useMe';

export const fetchRiskOverrideReasons = (page: number, size: number) => async (dispatch: Dispatch) => {
  try {
    const riskOverrideReasonPage = await finalRiskApi.findAllOverrideReasons({
      page,
      size,
    });
    dispatch(fetchRiskOverrideReasonPageSuccess(riskOverrideReasonPage));
  } catch (_) {
    //
  }
};

export const fetchFinalRiskLevels = () => async (dispatch: Dispatch) => {
  try {
    const finalRiskLevels = await finalRiskApi.findFinalRiskLevels();
    dispatch(fetchFinalRiskLevelsSuccess(finalRiskLevels));
  } catch (_) {
    //
  }
};

export const useRiskOverrideHistory = (personId: string) => {
  const { currentOrganisation } = useMe();
  return useQuery({
    queryKey: ['overrideHistory', currentOrganisation?.id, personId],
    queryFn: () => finalRiskApi.findOverrideHistory({ personId, page: 0, size: 40 }),
    enabled: currentOrganisation !== undefined,
    retry: false,
  });
};

export const usePersonRisks = (personId: string) => {
  const { currentOrganisation } = useMe();
  return useQuery({
    queryKey: ['personRisks', currentOrganisation?.id, personId],
    queryFn: () => riskApi.getPersonRisksInternal({ personId }),
    enabled: currentOrganisation !== undefined,
    retry: (failureCount, error) => error.status !== 404 && failureCount < DEFAULT_RETRY_COUNT,
  });
};

export const useFinalRiskHistory = (personId: string) => {
  const { currentOrganisation } = useMe();
  return useQuery({
    queryKey: ['finalRiskHistory', currentOrganisation?.id, personId],
    queryFn: () => riskApi.findFinalRiskHistory({ personId }),
    enabled: currentOrganisation !== undefined,
  });
};

export const useFinalRiskRanges = () => {
  const { currentOrganisation } = useMe();
  return useQuery({
    queryKey: ['finalRiskRanges', currentOrganisation?.id],
    queryFn: () => finalRiskApi.findFinalRiskLevelsV2(),
    enabled: currentOrganisation !== undefined,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};

const invalidateRiskQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  organisationId: number | undefined,
  personId: string
) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ['personRisks', organisationId, personId] }),
    queryClient.invalidateQueries({ queryKey: ['overrideHistory', organisationId, personId] }),
    queryClient.invalidateQueries({ queryKey: ['finalRiskHistory', organisationId, personId] }),
  ]);

export const useAddFinalRiskOverride = (personId: string) => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();
  return useMutation({
    mutationFn: (override: InternalApiAddFinalRiskOverride) =>
      finalRiskApi.addOverride({ personId, internalApiAddFinalRiskOverride: override }),
    onSuccess: () => invalidateRiskQueries(queryClient, currentOrganisation?.id, personId),
  });
};

export const useEndFinalRiskOverride = (personId: string) => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();
  return useMutation({
    mutationFn: () => finalRiskApi.endOverride({ personId }),
    onSuccess: () => invalidateRiskQueries(queryClient, currentOrganisation?.id, personId),
  });
};
